import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import React, { useMemo } from 'react'
import AccommodationCalendarMonthHeader from './AccommodationCalendarMonthHeader'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import AccommodationCalendarMonthGrid from './AccommodationCalendarMonthGrid'

export interface AccommodationMonthView {
  year: number;
  month: number;
  /**
   * The pricing data for this month
   * No pricing implies it is sold out
   */
  pricing?: App.Pricing;
  /**
   * What type of pricing data is being used
   * This will influence the display of the pricing
   */
  priceType: 'default' | 'member';
  duration: number;
  durationUnit: 'day' | 'night';
  /**
   * Label that will apear in top right hand corner of month.
   * Ensure only Label components are used
   */
  label?: React.ReactNode;
  /**
   * This month is a 'featured' month, for whatever reason.
   * Will apply the flame icon next to the month.
   */
  featured?: boolean;
  /**
   * Text that will appear when it's sold out
   * indicating that there is another action the user can take
   * Please handle this extra action manually with the on select
   */
  soldOutExtraActionText?: string;
}

interface AccommodationYearView {
  year: number;
  months: Array<AccommodationMonthView>;
}

interface Props {
  views: Array<AccommodationMonthView>;
  onSelect?: (view: AccommodationMonthView) => void;
  className?: string;
}

function AccommodationCalendarYear(props: Props) {
  const { views, className, onSelect } = props

  const sortedViews = useMemo<Array<AccommodationYearView>>(() => {
    const yearViews = groupBy(views, view => view.year).entries().map<AccommodationYearView>(([year, views]) => {
      return {
        year,
        months: views,
      }
    }).toArray()
    return sortBy(yearViews, view => view.year, 'asc')
  }, [views])

  return <VerticalSpacer className={className} gap={20}>
    {sortedViews.map(view => <VerticalSpacer gap={12} key={view.year}>
      <AccommodationCalendarMonthHeader year={view.year} />
      <AccommodationCalendarMonthGrid
        monthViews={view.months}
        onSelect={onSelect}
      />
    </VerticalSpacer>)}
  </VerticalSpacer>
}

export default AccommodationCalendarYear
